import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { api, ApiError } from '../api/client.js'
import { useAuth } from '../auth/AuthContext.js'
import { WeightInput, formatWeight } from '../components/WeightInput.js'
import { LrPrintModal } from './TripsPage.js'

interface Customer {
  id: string
  name: string
  requires_quotation_acceptance: boolean
}
interface Booking {
  id: string
  booking_no: string
  customer_name: string
  origin: string
  destination: string
  booking_type: 'FTL' | 'PTL'
  freight_amount: string | null
  status: string
}
/** One consignment as typed on the form (all strings; converted when saving). */
interface Consignment {
  consignor: string
  consignee: string
  material: string
  weight: string
  freight: string
}
const EMPTY: Consignment = { consignor: '', consignee: '', material: '', weight: '', freight: '' }

interface BookingItem {
  id: string
  consignor: string | null
  consignee: string | null
  material: string | null
  weight: string | null
  freight_allocated: string | null
}
interface LrRef {
  id: string
  lr_no: string
  booking_item_id: string | null
}
interface BookingDetailData extends Booking {
  material: string | null
  weight: string | null
  cancel_reason: string | null
  booking_items: BookingItem[]
  trips: { id: string; trip_no: string; status: string }[]
  lorry_receipts: LrRef[]
}

const rupees = (v: string | null | undefined) => (v ? `₹${v}` : '—')
const sumFreight = (list: Consignment[]) => list.reduce((t, c) => t + (c.freight ? Number(c.freight) : 0), 0)

/**
 * Stage 4 §3: booking confirmation for customers flagged to skip formal
 * quotation acceptance (Stage 4 §2.3) — a booking sourced FROM an accepted
 * quotation is created on the Enquiry screen instead (Stage 4 §3.1), where the
 * quotation actually lives.
 *
 * "+ New Booking" first asks FTL or PTL, then shows the matching form. FTL has
 * one consignment; PTL collects any number and lists them as they are added.
 * Opening a booking shows each consignment and its Lorry Receipt (LR) to print.
 */
export function BookingsPage() {
  const [bookings, setBookings] = useState<Booking[]>([])
  const [customers, setCustomers] = useState<Customer[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [openId, setOpenId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [bookingsRes, customersRes] = await Promise.all([
        api.get<{ rows: Booking[] }>('/bookings?pageSize=50'),
        api.get<{ rows: Customer[] }>('/customers?pageSize=100'),
      ])
      setBookings(bookingsRes.rows)
      setCustomers(customersRes.rows)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  async function cancelBooking(id: string) {
    const reason = window.prompt('Reason for cancelling (optional):') ?? undefined
    try {
      await api.post(`/bookings/${id}/cancel`, { reason })
      await load()
    } catch (err) {
      window.alert(err instanceof ApiError ? err.message : 'Cancel failed')
    }
  }

  const directCustomers = customers.filter((c) => !c.requires_quotation_acceptance)

  return (
    <div>
      <div className="tf-toolbar">
        <h2 style={{ margin: 0 }}>Bookings</h2>
        <button type="button" className="tf-button-primary" onClick={() => setFormOpen(true)}>
          + New Booking
        </button>
      </div>

      {error && <p className="tf-error">{error}</p>}

      <table className="tf-table">
        <thead>
          <tr>
            <th>Booking No.</th>
            <th>Customer</th>
            <th>Route</th>
            <th>Type</th>
            <th>Freight</th>
            <th>Status</th>
            <th aria-label="actions" />
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr>
              <td colSpan={7}>Loading…</td>
            </tr>
          )}
          {!loading && bookings.length === 0 && (
            <tr>
              <td colSpan={7}>No records found</td>
            </tr>
          )}
          {!loading &&
            bookings.map((b) => (
              <tr key={b.id}>
                <td>
                  <button type="button" className="tf-button-link" onClick={() => setOpenId(b.id)}>
                    {b.booking_no}
                  </button>
                </td>
                <td>{b.customer_name}</td>
                <td>
                  {b.origin} → {b.destination}
                </td>
                <td>{b.booking_type}</td>
                <td>{b.freight_amount ?? '—'}</td>
                <td>{b.status}</td>
                <td className="tf-row-actions">
                  <button type="button" onClick={() => setOpenId(b.id)}>
                    Open
                  </button>
                  {b.status !== 'Cancelled' && (
                    <button type="button" onClick={() => cancelBooking(b.id)}>
                      Cancel
                    </button>
                  )}
                </td>
              </tr>
            ))}
        </tbody>
      </table>

      {formOpen && (
        <NewBookingForm
          customers={directCustomers}
          onClose={() => setFormOpen(false)}
          onCreated={() => {
            setFormOpen(false)
            void load()
          }}
        />
      )}

      {openId && <BookingDetail id={openId} onClose={() => setOpenId(null)} />}
    </div>
  )
}

/** The five consignment boxes, shared by FTL (one) and PTL (the "add" box). */
function ConsignmentFields({
  value,
  onChange,
  freightLabel,
  required,
}: {
  value: Consignment
  onChange: (next: Consignment) => void
  freightLabel: string
  required: boolean
}) {
  const set = (patch: Partial<Consignment>) => onChange({ ...value, ...patch })
  return (
    <>
      <label className="tf-field">
        <span>Consignor (sender)</span>
        <input value={value.consignor} onChange={(e) => set({ consignor: e.target.value })} required={required} />
      </label>
      <label className="tf-field">
        <span>Consignee (receiver)</span>
        <input value={value.consignee} onChange={(e) => set({ consignee: e.target.value })} required={required} />
      </label>
      <label className="tf-field">
        <span>Material</span>
        <input value={value.material} onChange={(e) => set({ material: e.target.value })} />
      </label>
      <div className="tf-pair">
        <WeightInput label="Weight" valueKg={value.weight} onChangeKg={(kg) => set({ weight: kg })} required={required} />
        <label className="tf-field">
          <span>{freightLabel}</span>
          <input type="number" min="0" step="any" value={value.freight} onChange={(e) => set({ freight: e.target.value })} />
        </label>
      </div>
    </>
  )
}

function NewBookingForm({
  customers,
  onClose,
  onCreated,
}: {
  customers: Customer[]
  onClose: () => void
  onCreated: () => void
}) {
  const { currentBranchId } = useAuth()
  // null = still on the first step, "FTL or PTL?"
  const [bookingType, setBookingType] = useState<'FTL' | 'PTL' | null>(null)
  const [customerId, setCustomerId] = useState('')
  const [origin, setOrigin] = useState('')
  const [destination, setDestination] = useState('')
  const [totalFreight, setTotalFreight] = useState('') // PTL: whole-truck freight
  const [draft, setDraft] = useState<Consignment>(EMPTY) // FTL: the consignment; PTL: the one being added
  const [items, setItems] = useState<Consignment[]>([]) // PTL: consignments added so far
  const [error, setError] = useState<string | null>(null)
  const [pendingOverride, setPendingOverride] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const draftTouched = Object.values(draft).some((v) => v.trim() !== '')
  const allocated = sumFreight(items)

  function addConsignment() {
    if (!draft.consignor.trim() || !draft.consignee.trim()) {
      setError('Enter both the consignor and the consignee, then press Add.')
      return
    }
    if (!draft.weight) {
      setError('Enter the weight, then press Add.')
      return
    }
    setError(null)
    setItems((prev) => [...prev, draft])
    setDraft(EMPTY)
  }

  const toItem = (c: Consignment) => ({
    consignor: c.consignor.trim() || undefined,
    consignee: c.consignee.trim() || undefined,
    material: c.material.trim() || undefined,
    weight: c.weight || undefined,
    freight_allocated: c.freight || undefined,
  })

  function buildBody(list: Consignment[]) {
    const common = { customer_id: customerId, booking_type: bookingType, origin, destination }
    if (bookingType === 'FTL') {
      const c = list[0]!
      return {
        ...common,
        material: c.material.trim() || undefined,
        weight: c.weight || undefined,
        freight_amount: c.freight || undefined,
        booking_items: [toItem(c)],
      }
    }
    return { ...common, freight_amount: totalFreight || undefined, booking_items: list.map(toItem) }
  }

  async function submit(e: FormEvent, confirm = false) {
    e.preventDefault()
    setError(null)

    let list: Consignment[]
    if (bookingType === 'FTL') {
      list = [draft]
    } else {
      list = [...items]
      if (draftTouched) {
        if (!draft.consignor.trim() || !draft.consignee.trim() || !draft.weight) {
          setError('Finish the consignment you are typing (consignor, consignee and weight) and press Add, or clear it.')
          return
        }
        list.push(draft)
      }
      if (list.length === 0) {
        setError('Add at least one consignment.')
        return
      }
    }

    setSaving(true)
    try {
      const branchId = currentBranchId ?? (await api.get<{ id: string }[]>('/auth/branches'))[0]?.id
      await api.post(`/bookings${confirm ? '?confirm=true' : ''}`, { branch_id: branchId, ...buildBody(list) })
      setPendingOverride(null)
      onCreated()
    } catch (err) {
      if (err instanceof ApiError && err.requiresOverride) {
        setPendingOverride(err.message)
      } else {
        setError(err instanceof ApiError ? err.message : 'Failed to create booking')
      }
    } finally {
      setSaving(false)
    }
  }

  // Step 1: which kind of load?
  if (bookingType === null) {
    return (
      <div className="tf-slideover-backdrop" onClick={onClose}>
        <div className="tf-slideover" onClick={(e) => e.stopPropagation()}>
          <h2>New Booking</h2>
          <p className="tf-note">What kind of load is this?</p>
          <div className="tf-type-choice">
            <button type="button" onClick={() => setBookingType('FTL')}>
              <strong>FTL — Full truck load</strong>
              <span>One truck, one consignor and one consignee.</span>
            </button>
            <button type="button" onClick={() => setBookingType('PTL')}>
              <strong>PTL — Part truck load</strong>
              <span>Several consignments share one truck. Each gets its own LR.</span>
            </button>
          </div>
          <div className="tf-form-actions" style={{ marginTop: '1rem' }}>
            <button type="button" onClick={onClose}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Step 2: the form for that kind
  return (
    <div className="tf-slideover-backdrop" onClick={onClose}>
      <div className="tf-slideover" onClick={(e) => e.stopPropagation()}>
        <h2>
          New Booking · {bookingType}{' '}
          <button type="button" className="tf-button-link" onClick={() => setBookingType(null)}>
            change
          </button>
        </h2>
        {customers.length === 0 && (
          <p className="tf-note">
            No customers are flagged to skip quotation acceptance. Create bookings for other customers
            from their Accepted quotation on the Enquiries screen instead.
          </p>
        )}
        <form onSubmit={(e) => void submit(e)} className="tf-form">
          <label className="tf-field">
            <span>Customer</span>
            <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} required>
              <option value="" disabled>
                Select…
              </option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label className="tf-field">
            <span>Origin</span>
            <input value={origin} onChange={(e) => setOrigin(e.target.value)} required />
          </label>
          <label className="tf-field">
            <span>Destination</span>
            <input value={destination} onChange={(e) => setDestination(e.target.value)} required />
          </label>

          {bookingType === 'FTL' && (
            <ConsignmentFields value={draft} onChange={setDraft} freightLabel="Freight amount (₹)" required />
          )}

          {bookingType === 'PTL' && (
            <>
              <label className="tf-field">
                <span>Total freight for the whole truck (₹)</span>
                <input type="number" min="0" step="any" value={totalFreight} onChange={(e) => setTotalFreight(e.target.value)} />
              </label>

              <div className="tf-consignment-box">
                <strong>Add a consignment</strong>
                <ConsignmentFields value={draft} onChange={setDraft} freightLabel="Freight (₹)" required={false} />
                <button type="button" className="tf-button-primary" onClick={addConsignment}>
                  + Add
                </button>
              </div>

              {items.length > 0 && (
                <div className="tf-consignment-list">
                  <strong>Consignments on this truck ({items.length})</strong>
                  {items.map((c, i) => (
                    <div key={i} className="tf-consignment-card">
                      <div>
                        <strong>
                          {i + 1}. {c.consignor} → {c.consignee}
                        </strong>
                        <div className="tf-note">
                          {[c.material, c.weight ? formatWeight(c.weight) : '', c.freight ? `₹${c.freight}` : '']
                            .filter(Boolean)
                            .join(' · ') || 'No other details'}
                        </div>
                      </div>
                      <button type="button" onClick={() => setItems((prev) => prev.filter((_, idx) => idx !== i))}>
                        Remove
                      </button>
                    </div>
                  ))}
                  {totalFreight && (
                    <p className="tf-note">
                      Allocated ₹{allocated} of ₹{totalFreight}
                      {Math.abs(allocated - Number(totalFreight)) > 0.01 && ' — the split does not add up to the total'}
                    </p>
                  )}
                </div>
              )}
            </>
          )}

          {error && <p className="tf-error">{error}</p>}

          {pendingOverride && (
            <div className="tf-override-banner">
              <p>{pendingOverride}</p>
              <button type="button" className="tf-button-primary" disabled={saving} onClick={(e) => void submit(e, true)}>
                {saving ? 'Saving…' : 'Override & Confirm'}
              </button>
            </div>
          )}

          <div className="tf-form-actions">
            <button type="submit" className="tf-button-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

/** Opens one booking: its consignments (click to expand) and each one's LR. */
function BookingDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const [data, setData] = useState<BookingDetailData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [openIds, setOpenIds] = useState<Set<string>>(new Set())
  const [printLrId, setPrintLrId] = useState<string | null>(null)

  useEffect(() => {
    api
      .get<BookingDetailData>(`/bookings/${id}`)
      .then((d) => {
        setData(d)
        // A full truck load has just one consignment, so show it already open.
        if (d.booking_type === 'FTL') setOpenIds(new Set(d.booking_items.map((i) => i.id)))
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Failed to load booking'))
  }, [id])

  function toggle(itemId: string) {
    setOpenIds((prev) => {
      const next = new Set(prev)
      if (next.has(itemId)) next.delete(itemId)
      else next.add(itemId)
      return next
    })
  }

  // PTL: one LR per consignment. FTL: the trip's single LR (not tied to an item).
  const lrFor = (item: BookingItem): LrRef | undefined =>
    data?.lorry_receipts.find((l) => (data.booking_type === 'PTL' ? l.booking_item_id === item.id : l.booking_item_id === null))

  return (
    <>
    <div className="tf-slideover-backdrop" onClick={onClose}>
      <div className="tf-slideover" onClick={(e) => e.stopPropagation()}>
        {error && <p className="tf-error">{error}</p>}
        {!data && !error && <p className="tf-note">Loading…</p>}
        {data && (
          <>
            <h2>
              {data.booking_no} · {data.booking_type}
            </h2>
            <p className="tf-note">
              {data.customer_name}
              <br />
              {data.origin} → {data.destination}
              <br />
              Status: {data.status}
              {data.cancel_reason ? ` (${data.cancel_reason})` : ''}
              <br />
              {data.booking_type === 'PTL' ? 'Total freight' : 'Freight'}: {rupees(data.freight_amount)}
              {data.trips.length > 0 && (
                <>
                  <br />
                  Trip: {data.trips.map((t) => `${t.trip_no} (${t.status})`).join(', ')}
                </>
              )}
            </p>

            <h2 style={{ marginTop: '1.25rem' }}>
              {data.booking_type === 'PTL' ? `Consignments (${data.booking_items.length})` : 'Consignment'}
            </h2>
            {data.booking_items.length === 0 && (
              <p className="tf-note">
                No consignor or consignee was entered on this booking. Type them when you generate the LR on the trip.
              </p>
            )}
            <div className="tf-consignment-list">
              {data.booking_items.map((item, i) => {
                const lr = lrFor(item)
                const isOpen = openIds.has(item.id)
                return (
                  <div key={item.id} className="tf-consignment-card tf-consignment-open">
                    <button type="button" className="tf-consignment-head" onClick={() => toggle(item.id)} aria-expanded={isOpen}>
                      <span>
                        {isOpen ? '▾' : '▸'} {i + 1}. {item.consignor ?? '—'} → {item.consignee ?? '—'}
                      </span>
                      <span className="tf-note">{lr ? `LR ${lr.lr_no}` : 'No LR yet'}</span>
                    </button>
                    {isOpen && (
                      <div className="tf-consignment-body">
                        <dl className="tf-stat-list">
                          <div>
                            <dt>Consignor</dt>
                            <dd>{item.consignor ?? '—'}</dd>
                          </div>
                          <div>
                            <dt>Consignee</dt>
                            <dd>{item.consignee ?? '—'}</dd>
                          </div>
                          <div>
                            <dt>Material</dt>
                            <dd>{item.material ?? '—'}</dd>
                          </div>
                          <div>
                            <dt>Weight</dt>
                            <dd>{formatWeight(item.weight)}</dd>
                          </div>
                          <div>
                            <dt>Freight</dt>
                            <dd>{rupees(item.freight_allocated)}</dd>
                          </div>
                        </dl>
                        {lr ? (
                          <div className="tf-form-actions">
                            <button type="button" className="tf-button-primary" onClick={() => setPrintLrId(lr.id)}>
                              Print LR {lr.lr_no}
                            </button>
                          </div>
                        ) : (
                          <p className="tf-note">
                            The LR is made on the trip. Create the trip, assign the vehicle, then use Generate LR on the
                            Trips screen. It will appear here to print.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </>
        )}
        <div className="tf-form-actions" style={{ marginTop: '1rem' }}>
          <button type="button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
    {/* Outside the backdrop, so closing the print window does not also close this booking. */}
    {printLrId && <LrPrintModal lrId={printLrId} onClose={() => setPrintLrId(null)} />}
    </>
  )
}
